import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useBatchAnalyze } from '../../hooks/useBatchAnalyze';
import { useSessions } from '../../hooks/useSessions';
import { type SessionSummary } from '../../services/api/sessions';
import { listTickets } from '../../services/api/tickets';
import { cn } from '../../utils/cn';

import {
  findActivePresetKey,
  presetRange,
  rangeSpanDays,
  stockholmToday,
  type SessionRange,
} from './date-range';
import { KvalitetPanel } from './KvalitetPanel';
import { SessionList } from './SessionList';
import { SessionLogViewer } from './SessionLogViewer';
import { SessionQualityFilters } from './SessionQualityFilters';
import { SessionRangeToolbar } from './SessionRangeToolbar';
import {
  countBatchCandidates,
  countQuality,
  filterSessions,
  type QualityFilter,
} from './session-filters';
import { parseFocusTicket, parseSessionsTab, searchParamsForTab } from './sessions-tab';
import { SessionTabs } from './SessionTabs';
import { TicketsPanel } from './TicketsPanel';

// Idag som förval: 30 dagar är över tusen sessioner, vilket både tar tid att
// hämta och kapas av listgränsen innan man hunnit titta på dagens.
const DEFAULT_RANGE_DAYS = 0;

export const SessionsPage = () => {
  const today = stockholmToday();
  const [range, setRange] = useState<SessionRange>(() => presetRange(today, DEFAULT_RANGE_DAYS));
  const [includePlayground, setIncludePlayground] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [qualityFilter, setQualityFilter] = useState<QualityFilter>('all');
  const [showEmpty, setShowEmpty] = useState(false);
  const [selectedSession, setSelectedSession] = useState<SessionSummary | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // useSearchParams ger tillbaka samma instans mellan renderingar. Muteras den
  // direkt följer ändringen med in i nästa uppdatering, även den som ångrades.
  const updateSearchParams = (
    change: (next: URLSearchParams) => void,
    options?: { replace?: boolean },
  ) => {
    const next = new URLSearchParams(searchParams);
    change(next);
    setSearchParams(next, options);
  };

  const deepLinkSessionId = searchParams.get('session');
  const activeTab = parseSessionsTab(searchParams);
  // Panelen läser ticketen bara när den monteras. Nyckeln monterar om den när en
  // länk pekar ut en annan ticket medan fliken redan är öppen.
  const focusTicketId = parseFocusTicket(searchParams);
  const draftCountQuery = useQuery({
    queryKey: ['tickets-count-draft'],
    queryFn: () => listTickets({ status: 'draft', limit: 1 }),
    staleTime: 30_000,
  });
  const draftCount = draftCountQuery.data?.total ?? 0;
  const { sessions, unavailableReason, total, truncated, isLoading, isFetching, error, sync } =
    useSessions({ ...range, includePlayground, includeEmpty: showEmpty });
  const isSyncing = isLoading || isFetching;

  const spanDays = useMemo(() => rangeSpanDays(range.fromDate, range.toDate), [range]);
  const activePreset = useMemo(
    () => findActivePresetKey(range.fromDate, range.toDate, today),
    [range, today],
  );

  const unanalyzedCount = useMemo(() => countBatchCandidates(sessions), [sessions]);
  const batch = useBatchAnalyze({ onDone: sync });

  useEffect(() => {
    void sync();
  }, [range, includePlayground, showEmpty, sync]);

  const qualityCounts = useMemo(() => countQuality(sessions), [sessions]);

  const filteredSessions = useMemo(
    () => filterSessions(sessions, { search: searchQuery, quality: qualityFilter }),
    [sessions, searchQuery, qualityFilter],
  );

  return (
    <section className="space-y-6">
      <header>
        <h2 className="text-3xl font-semibold text-brand-blue font-display">Sessioner</h2>
        <p className="text-sm text-text-grey mt-1">
          Visa alla chat-sessioner från de senaste dagarna. Klicka på en session för att se
          detaljerade loggar.
        </p>
      </header>

      <SessionTabs
        active={activeTab}
        draftCount={draftCount}
        onSelect={(tab) => setSearchParams(searchParamsForTab(searchParams, tab), { replace: true })}
      />

      {activeTab === 'kvalitet' ? (
        <KvalitetPanel />
      ) : activeTab === 'tickets' ? (
        <TicketsPanel
          key={focusTicketId ?? 'ingen'}
          focusTicketId={focusTicketId}
          onOpenSession={(sid) => {
            const next = searchParamsForTab(searchParams, 'sessions');
            next.set('session', sid);
            setSearchParams(next);
          }}
        />
      ) : (
        <>
          <div className="space-y-4">
            <SessionRangeToolbar
              range={range}
              today={today}
              activePreset={activePreset}
              onChange={(patch) => setRange((current) => ({ ...current, ...patch }))}
              onPreset={(days) => setRange(presetRange(today, days))}
              batch={{
                count: unanalyzedCount,
                limit: batch.limit,
                isRunning: batch.status.loading,
                onRun: () => void batch.run(spanDays),
              }}
              sync={{ isRunning: isSyncing, onRun: () => void sync() }}
            />

            {batch.status.message && (
              <div
                className={cn(
                  'rounded-lg border px-3 py-2 text-sm',
                  batch.status.isError
                    ? 'border-red-200 bg-red-50 text-red-800'
                    : 'border-indigo-200 bg-indigo-50 text-indigo-800',
                )}
              >
                {batch.status.message}
              </div>
            )}

            {unavailableReason ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900">
                    Sessioner är inte tillgängliga just nu
                  </h3>
                  <p className="text-sm text-amber-800 mt-1">{unavailableReason}</p>
                </div>
              </div>
            ) : null}

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Sök session-ID..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <SessionQualityFilters
              counts={qualityCounts}
              total={sessions.length}
              value={qualityFilter}
              onChange={setQualityFilter}
              showEmpty={showEmpty}
              onToggleEmpty={() => setShowEmpty((value) => !value)}
              includePlayground={includePlayground}
              onTogglePlayground={() => setIncludePlayground((value) => !value)}
            />

            {truncated && total !== null && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Visar {sessions.length} av {total} sessioner. Begränsa tidsperioden för att se
                resten.
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <SessionList
                isLoading={isLoading}
                error={error}
                unavailableReason={unavailableReason}
                sessions={sessions}
                visible={filteredSessions}
                selectedId={selectedSession?.sessionId}
                onSelect={setSelectedSession}
              />
            </div>

            <div className="lg:col-span-2">
              {selectedSession || deepLinkSessionId ? (
                <SessionLogViewer
                  sessionId={selectedSession?.sessionId ?? deepLinkSessionId!}
                  onClose={() => {
                    setSelectedSession(null);
                    if (deepLinkSessionId) {
                      updateSearchParams((next) => next.delete('session'), { replace: true });
                    }
                  }}
                />
              ) : (
                <div className="h-full bg-white rounded-lg border border-slate-200 flex items-center justify-center text-slate-500">
                  <p className="text-center">
                    Välj en session från listan för att se detaljerade loggar →
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
};
